import { Control, Controller } from "react-hook-form"
import { Trans, t } from "@lingui/macro"
import { useCallback, useEffect, useMemo, useState } from "react"

import { ExternalLink } from "../../style"
import { TextField } from "../../../../../../components/TextField"
import { i18n } from "@lingui/core"

type LocaleConfig = {
  [key: string]: {
    maxLength: number
    inputRegEx: RegExp
    mask?: (value: string) => string
    formatValidation?: {
      regEx: RegExp
      message: string
    }
  }
}

export type OnChangePostalCodeParams = {
  value: string
  postalCodeLenght: number
}

interface PostalCodeControllerProps {
  name: string
  errorMsg?: string
  control: Control<any>
  disabled?: boolean
  onChangePostalCode?: (params: OnChangePostalCodeParams) => void
  hasLocationError?: boolean
  defaultValue?: string
}

export function PostalCodeController({
  name,
  errorMsg,
  control,
  disabled,
  onChangePostalCode,
  hasLocationError,
  defaultValue = "",
}: PostalCodeControllerProps) {
  const [_value, _setValue] = useState<string>("")

  const LOCALE_CONFIG: LocaleConfig = useMemo(
    () => ({
      "es-AR": {
        maxLength: 4,
        inputRegEx: /[^\d]/gi,
      },
      "es-MX": {
        maxLength: 5,
        inputRegEx: /[^\d]/gi,
      },
      "pt-BR": {
        maxLength: 8,
        inputRegEx: /[^\d]/gi,
        mask: (value: string) => {
          if (!value) return ""

          if (value.length > 5)
            return `${value.slice(0, 5)}-${value.slice(
              5,
              Math.min(value.length, 8)
            )}`

          return value
        },
        formatValidation: {
          regEx: /^\d{8}$/,
          message: t`Usá el formato XXXXX-XXX`,
        },
      },
    }),
    []
  )

  const mask = useCallback(
    (value: string) => {
      const maskFunction = LOCALE_CONFIG[i18n.locale]?.mask

      if (!maskFunction) return value
      return maskFunction(value)
    },
    [LOCALE_CONFIG]
  )

  const parseField = useCallback(
    (value: string) => {
      return value
        .replace(LOCALE_CONFIG[i18n.locale].inputRegEx, "")
        .slice(0, LOCALE_CONFIG[i18n.locale].maxLength)
    },
    [LOCALE_CONFIG]
  )

  useEffect(() => {
    if (defaultValue) _setValue(mask(parseField(defaultValue)))
  }, [defaultValue, mask, parseField])

  return (
    <>
      <Controller
        control={control}
        name={name}
        defaultValue=""
        rules={{
          required: t`Completá el campo código postal.`,
          validate: {
            checkLocationError: () =>
              !hasLocationError ||
              t`No pudimos encontrar el código postal. Revisá e intentá otra vez.`,
            checkCorrectFormat: checkCorrectFormat,
          },
        }}
        render={({ onChange }) => (
          <TextField
            name={name}
            label={<Trans>Código Postal</Trans>}
            errorMsg={errorMsg}
            hideLength={true}
            onChange={e => {
              const parsedValue = parseField(e.target.value)
              onChange(parsedValue)
              _setValue(mask(parsedValue))
              if (onChangePostalCode)
                onChangePostalCode({
                  value: parsedValue,
                  postalCodeLenght: LOCALE_CONFIG[i18n.locale].maxLength,
                })
            }}
            value={_value}
            disabled={disabled}
          />
        )}
      />

      <ExternalLink
        rel="noopener noreferrer"
        href={t`https://www.correoargentino.com.ar/formularios/cpa`}
        target="_blank"
      >
        <Trans>No conozco mi código postal</Trans>
      </ExternalLink>
    </>
  )

  function checkCorrectFormat(value: string): string | boolean {
    const formatValidation =
      LOCALE_CONFIG[i18n.locale]?.formatValidation ?? null
    if (!value || !formatValidation) return true

    const { regEx, message } = formatValidation

    return regEx.test(value) || message
  }
}
